import React, { useCallback, useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import type { KeenSliderOptions } from 'keen-slider/react';
import { useKeenSlider } from 'keen-slider/react';
import { theme } from '@components-shared/src/stitches';
import { type ColumnType, useCarouselColumnAmount } from '@components/layout/useColumnAmount';
import { useMediaQuery } from '@context/mediaQuery';
import { useIsSsr } from '@hooks/useIsSsr';
import { useSafeLayoutEffect } from '@hooks/useSafeLayoutEffect';
import { CarouselArrow } from './CarouselArrow';

// Use vertical padding to avoid cutting off box shadows on slides
const PADDING = 24;
const LOAD_MORE_THRESHOLD = 6;

export const SSR_SLIDE_WIDTH_DEFAULT =
  'calc(((100% - ((var(--columns) - 1) * var(--gap))) / (var(--columns))))';
export const SSR_SLIDE_WIDTH_DEFAULT_FEED =
  'calc(((100% - ((var(--feed-columns) - 1) * var(--gap))) / (var(--feed-columns))))';

const SSR_SLIDE_WIDTH_DESKTOP =
  'calc(((100% - ((var(--columns)) * var(--gap))) / (var(--columns) + 1)))';

// TODO: Move to tailwind https://linear.app/sound/issue/ENG-7801/move-gridcarousel-to-tailwind
/*
 * This carousel can be used next to Sound's default grid
 * It uses the same column amount as the grid, and is responsive
 * It also has a fade mask to hide the overflow
 *
 * Modeled after services/webapp/components/basics/Carousel/index.tsx
 * Note: this carousel does not use virtualization, to keep things light, performant and potentially SSR proof
 * This should be ok because we don't expect users to load thousands of slides 1 click or horizontal scroll at a time
 * */
export const GridCarousel = ({
  slides,
  onEndReached,
  columnAmount: columnAmountProp,
  hideArrows = false,
  externalActiveIndex,
  onInternalActiveIndexChange,
  spacing,
  alignSlidesLeft = false,
  ssrSlideWidth,
  columnType = 'default',
  arrowClassName,
}: {
  /*
   * slides is an array of objects with an id and content
   * */
  slides: {
    id: string;
    slide: React.ReactNode;
  }[];
  /*
   * Trigger a fetch more function when the last page is reached
   * Useful for infinite scroll
   * */
  onEndReached: (() => void) | null;
  /*
   * If you want to trigger a function when active index changes
   * */
  onInternalActiveIndexChange: ((activeIndex: number) => void) | null;
  /*
   * If you want to set a custom column amount
   * */
  columnAmount: number | null;
  /*
   * If you want to hide the arrows
   * */
  hideArrows: boolean;
  /*
   * If you want to set a custom current index
   * Useful if auto scrolling
   * */
  externalActiveIndex: number | null;
  /*
   * If you want to set a custom spacing between slides
   * */
  spacing: number | null;
  /*
   * Align all including last slide left
   * */
  alignSlidesLeft: boolean;
  /*
   * Slide width on server side, when window and refs are not available
   * @note: use css only, for server side rendering
   * @default: 'calc(((100% - ((var(--columns) - 1) * var(--gap))) / var(--columns)))' (grid size)
   * */
  ssrSlideWidth: string | undefined;
  // Default column type will have 5 cards per row, large will have 5 cards per row
  columnType: ColumnType;
  // Class name for the arrow
  arrowClassName?: string;
}) => {
  const [loaded, setLoaded] = React.useState(false);
  const [internalActiveIndex, setInternalActiveIndex] = React.useState<number>(0);
  const [externalSwipeCompleted, setExternalSwipeCompleted] = React.useState(true);
  const gridColumnAmount = useCarouselColumnAmount({ columnType });
  const isSsr = useIsSsr();

  const { tablet } = useMediaQuery();

  const ssrSlideWidthComputed =
    ssrSlideWidth ?? (tablet ? SSR_SLIDE_WIDTH_DESKTOP : SSR_SLIDE_WIDTH_DEFAULT);

  // Use prop if set, otherwise use our default grid column amount
  const columnAmount = columnAmountProp || gridColumnAmount;

  const keenSliderOptions: KeenSliderOptions = useMemo(
    () => ({
      slideChanged(slider) {
        if (!slider.track) return;

        setInternalActiveIndex(slider.track.details.rel);

        if (slider.track.details.rel >= slides.length - LOAD_MORE_THRESHOLD) {
          onEndReached?.();
        }
      },
      mode: 'snap',
      rtl: false,
      slides: {
        origin: alignSlidesLeft ? 0 : undefined,
        perView: columnAmount,
        spacing: spacing !== null ? spacing : 20,
      },
      range: {
        align: true,
        min: 0,
        max: slides.length - 1,
      },
      initial: 0,
      created() {
        setLoaded(true);
      },
    }),
    [alignSlidesLeft, columnAmount, onEndReached, slides.length, spacing],
  );

  const [sliderRef, instanceRef] = useKeenSlider<HTMLDivElement>(keenSliderOptions);
  const [columnWidth, setColumnWidth] = useState(0);
  // Hide arows if columnWidth is not set yet to avoid jumping arrows
  const disablePrev = columnWidth === 0 || internalActiveIndex === 0;
  const disableNext =
    columnWidth === 0 || (slides && internalActiveIndex + columnAmount >= slides.length);

  const carouselWrapperRef = React.useRef<HTMLDivElement>(null);

  const onLeftArrowClick = useCallback(
    (event?: React.MouseEvent) => {
      event?.stopPropagation();
      instanceRef.current?.prev();
    },
    [instanceRef],
  );

  const onRightArrowClick = useCallback(
    (event?: React.MouseEvent) => {
      event?.stopPropagation();
      instanceRef.current?.next();
    },
    [instanceRef],
  );

  useEffect(() => {
    if (!carouselWrapperRef.current) return;

    const newColumnWidth =
      (carouselWrapperRef.current.clientWidth -
        Number(theme.space[5].value.split('px')[0]) * (columnAmount - 1)) /
      columnAmount;

    setColumnWidth(newColumnWidth);
  }, [columnAmount, carouselWrapperRef]);

  // If externalActiveIndex is more than 1 apart from slider's current index, it means something external triggered a swipe
  // In that case, we need to prevent in between slides from triggerening onInternalActiveIndexChange
  // This is because the slider will trigger slideChanged for each slide in between, which will set externalActiveIndex to that slide and stop the swipe prematurely
  useSafeLayoutEffect(() => {
    if (Math.abs((externalActiveIndex || 0) - internalActiveIndex) > 1) {
      setExternalSwipeCompleted(false);
    } else if (externalActiveIndex === internalActiveIndex) {
      setExternalSwipeCompleted(true);
    }
  }, [externalActiveIndex, internalActiveIndex, instanceRef]);

  // If external swipe is complete, report active index change
  // This helps parent keep track of active index to show in indicators
  useSafeLayoutEffect(() => {
    if (externalSwipeCompleted) {
      onInternalActiveIndexChange?.(internalActiveIndex);
    }
  }, [externalSwipeCompleted, internalActiveIndex, onInternalActiveIndexChange]);

  // Update slider when slide ids change
  // This avoids side effects where slides appear in the wrong position, and at strage sizes
  const slideIds = useMemo(() => slides.map(slide => slide.id), [slides]);
  const firstSlideId = slides?.[0]?.id;

  useSafeLayoutEffect(() => {
    instanceRef.current?.update(keenSliderOptions);
  }, [instanceRef, keenSliderOptions, slideIds]);

  // If id first slide changes, move to first slide
  // This helps us spot when eg filters are used to update content
  useSafeLayoutEffect(() => {
    if (!firstSlideId) return;
    // Snap with 0 duration to avoid animation
    // There's a chance content updates mid slide, which will cause scroll to stop in an unexepected place
    instanceRef.current?.moveToIdx(0, true, { duration: 0 });
  }, [firstSlideId, instanceRef]);

  useSafeLayoutEffect(() => {
    if (!externalActiveIndex && externalActiveIndex !== 0) return;
    instanceRef.current?.moveToIdx(externalActiveIndex, true, {
      // Use slighly longer transition to slide back to slide 0
      duration: externalActiveIndex === 0 ? 1000 : 500,
    });
  }, [externalActiveIndex, instanceRef]);

  return (
    <div className="relative" ref={carouselWrapperRef}>
      <CarouselContainer>
        <div
          // Avoid using virtuoso on server side
          // It needs access to window to work, and wil cause a flash of unstyled content
          ref={isSsr ? null : sliderRef}
          style={{ gridTemplateColumns: `repeat(99, ${ssrSlideWidthComputed})` }}
          className={clsx(
            isSsr
              ? 'grid w-full grid-cols-12 gap-5 overflow-x-visible'
              : 'keen-slider [&.keen-slider:not([data-keen-slider-disabled])]:overflow-visible',
          )}
          aria-label="carousel"
          data-feed-clickable-div
        >
          {slides?.map(slide => (
            <div className="keen-slider__slide overflow-visible" key={slide.id}>
              {slide.slide}
            </div>
          ))}
        </div>
      </CarouselContainer>
      {!hideArrows && tablet && loaded && columnAmount < slides.length && (
        <>
          <ArrowContainer
            direction="left"
            disabled={disablePrev}
            onClick={onLeftArrowClick}
            className={arrowClassName}
          >
            <CarouselArrow ariaLabel="Previous slide" disabled={disablePrev} />
          </ArrowContainer>
          <ArrowContainer
            direction="right"
            disabled={disableNext}
            onClick={onRightArrowClick}
            className={arrowClassName}
          >
            <CarouselArrow ariaLabel="Next slide" disabled={disableNext} />
          </ArrowContainer>
        </>
      )}
    </div>
  );
};

const CarouselContainer = ({ className, ...rest }: React.HTMLProps<HTMLDivElement>) => {
  return (
    <div
      className={clsx(
        `w-[calc(100%+${
          PADDING * 2
        }px)] p-[${PADDING}px] -m-[${PADDING}px] [&:not([data-keen-slider-disabled])>*>*]:!overflow-visible`,
        `md:-mx-40 md:w-[calc(100%+theme(spacing.40)*2)] md:px-40`,
        // negative margin top to keep the mask-image from cutting off the top of elements that break out of the slide (e.g. golden egg)
        `lg:-mx-5 lg:-mt-4 lg:w-[calc(100%+theme(spacing.5)*2)] lg:px-5 lg:pt-4 lg:[mask-image:linear-gradient(90deg,_transparent,_black_2%,_black_98%,_transparent)] lg:[scroll-padding:0px_theme(spacing.5)]`,
        className,
      )}
      {...rest}
    />
  );
};

const ArrowContainer = ({
  direction,
  disabled = false,
  className,
  ...rest
}: React.HTMLProps<HTMLDivElement> & { direction: 'left' | 'right'; disabled?: boolean }) => {
  return (
    <div
      className={clsx(
        `z-10 absolute top-1/2 transform rounded-full bg-white transition-opacity duration-100 ease-in-out`,
        direction === 'left' && `left-6 -translate-x-1/2 -translate-y-1/2 rotate-90 md:left-1`,
        direction === 'right' && `right-6 -translate-y-1/2 translate-x-1/2 -rotate-90 md:right-1`,
        disabled && `opacity-0 hover:opacity-50`,
        className,
      )}
      {...rest}
    />
  );
};
